'use client';

import {useState, useEffect, useContext, useRef} from 'react';
import { useComponentLogger } from '@/lib/component-logger';
import DataContext from '@/contexts/DataContext';
import { fetchExprData, fetchTooltip, extractTrackedParams, fetchAPIData, extractChangedParams } from '@/lib/dataHandlers/DataFetchers';
import { useInterval } from '@/hooks/use-interval';

import { textColors } from '@/styles/theme/colors';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { OverlayDialog } from './overlay-dialog';


/**
 * Displays a an image
 * @param {} param0 
 * @returns 
 */
export function Overlay({ config={} }) {
    const logger = useComponentLogger("Overlay");
    // const [imgSrc, setImgSrc] = useState('assets/prototypes/Napoleon-Schematic.png');
    const { currentProject, selectedViewParams, changedParams } = useContext(DataContext);
    const [imgSrc, setImgSrc] = useState(null);
    const [overlays, setOverlays] = useState({});
    const [refreshInterval, setRefreshInterval] = useState(0);
    const trackedParamValsRef = useRef({});
    const trackedParamNamesRef = useRef({}); // names of the params that this component depends on, extracted from its api expr


    logger.debug('overlay config', config, selectedViewParams);

    const handleChildOpen = () => {
        fetchContent().catch(() => {});
    };


    const fetchContent = async () => {
        if (config.dataSource === null || config.dataSource === undefined) return;

        const dataSource = config.dataSource;

        if (dataSource.type === "apiExpr") {
            const data = await fetchExprData(currentProject, selectedViewParams, dataSource.expr);
            logger.debug('overlay api data', data.rows, selectedViewParams, refreshInterval);

            if (data?.rows && data.rows.length > 0) {
                let config = data.rows[0];

                if (config.imageUri ) await fetchImage(config.imageUri);
                if (config.overlay) {
                    setOverlays(config.overlay);
                } else setOverlays([]);
                if (config.interval && config.interval.val) {
                    setRefreshInterval(config.interval.val * 1000);
                } else setRefreshInterval(null);
                
            }
        }

    }

    const fetchImage = async (url) => {
        const imgURL = url.val ?? url.value ?? url;
        logger.debug('img start', imgURL)
        const data = await fetchAPIData(imgURL, '', false);
        logger.debug('img api data', data);
        //since the img is received as based64 in the res
        if (typeof data === 'string' && data?.startsWith('iVBOR')) {
            // I added this to handle the different schemes for images
            setImgSrc(`data:image/png;base64,${data}`);
        }
    }

    useEffect(() => {
        let trackedParams = extractTrackedParams(config.dataSource);
        let params = {};
        for (const param of trackedParams) {
            if (selectedViewParams[param]) params[param] = selectedViewParams[param].value;
        }

        logger.debug('tracked params', params, trackedParams);
        trackedParamValsRef.current = params;
        trackedParamNamesRef.current = trackedParams;
        fetchContent().catch(() => {});
    }, []);

    useEffect(() => {
        let shouldReFetch = extractChangedParams(trackedParamNamesRef.current, changedParams, selectedViewParams, trackedParamValsRef.current);
        logger.debug('changed params', changedParams, trackedParamNamesRef.current, shouldReFetch, selectedViewParams);
        if (shouldReFetch.changed) {
            trackedParamValsRef.current = shouldReFetch.paramRef;
            fetchContent();
        }

        // make sure to clear the content if any of the tracked params were removed
        // common use case: when a parent param changes, it clears the child param
        for (const param of trackedParamNamesRef.current) { 
            if (!selectedViewParams[param]) {
                setImgSrc(null);
                setOverlays([]);
                return;
            }
        }
    }, [changedParams]);


    // Start the interval only once we have a refreshInterval > 0
    useInterval(fetchContent, refreshInterval || null);

    return (
        <>
        <Box
            sx={{
                position: 'relative', // tells children to base their absolute coords on this parent
                display: 'inline-block', // shrink box to the exact same size as its contained img
                width: '100%', 
                // maxWidth: 600, 
                // padding: '1rem',
            }}
            >
            <Box
                component="img"
                src={imgSrc}
                sx={{
                    width: '100%',
                    height: 'auto',
                    display: 'block', // expand to the full width of the parent
                    // filter: 'invert(1)'
                }}
            />
            {Object.entries(overlays).map(([key, value], index) => (<OverlayDialog key={index} config={value} notifyParentOnOpen={handleChildOpen} />) )}
        </Box>
        </>
    );
}